import React, { useEffect, useState } from "react";
import axios from "../../utils/axiosInstance";
import { useNavigate } from "react-router-dom";

const API_BASE = (process.env.REACT_APP_API_URL || "").replace(/\/+$/, "");

function fileUrl(path) {
  if (!path) return null;
  if (String(path).startsWith("http")) return path;
  return `${API_BASE}/uploads/ids/${path}`;
}

export default function KYCReviewsPage() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);

  const fetchKyc = async () => {
    try {
      setLoading(true);
      const res = await axios.get("/api/admin/kyc/pending");
      setUsers(res.data?.data || []);
    } catch (err) {
      console.error(err.response?.data || err.message);
      alert("Erreur lors du chargement des KYC.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchKyc();
  }, []);

  const updateStatus = async (userId, status) => {
    try {
      setUpdatingId(userId);
      await axios.put(`/api/admin/users/${userId}/kyc`, { status });
      setUsers((prev) => prev.filter((u) => u.id !== userId));
    } catch (err) {
      console.error(err.response?.data || err.message);
      alert("Erreur lors de la mise à jour du KYC.");
    } finally {
      setUpdatingId(null);
    }
  };

  return (
    <div className="pt-6 px-4 md:ml-64 min-h-screen">
      <div className="max-w-7xl mx-auto space-y-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
            KYC Reviews
          </h1>
          <p className="text-sm text-gray-500">
            Vérifiez et approuvez les documents d'identité des utilisateurs.
          </p>
        </div>

        {loading ? (
          <p className="text-gray-500">Chargement...</p>
        ) : users.length === 0 ? (
          <div className="rounded-2xl border bg-white dark:bg-gray-800 p-6 text-gray-500">
            Aucun KYC en attente.
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {users.map((user) => {
              const p = user.profile || {};
              const front = fileUrl(p.documentFrontUrl || p.nationalIdUrl);
              const back = fileUrl(p.documentBackUrl);

              return (
                <div
                  key={user.id}
                  className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5 shadow-sm"
                >
                  <div className="flex justify-between gap-4">
                    <div>
                      <h3 className="font-semibold text-gray-900 dark:text-white">
                        {user.fullName || user.email}
                      </h3>
                      <p className="text-sm text-gray-500">{user.email}</p>
                      <p className="text-sm text-gray-500">
                        {user.phoneNumber || "—"}
                      </p>
                    </div>

                    <button
                      onClick={() => navigate(`/admin/users/${user.id}`)}
                      className="text-sm px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-700"
                    >
                      Voir profil
                    </button>
                  </div>

                  <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-gray-500">Document :</span>{" "}
                      <strong>{p.documentType || "—"}</strong>
                    </div>
                    <div>
                      <span className="text-gray-500">Numéro :</span>{" "}
                      <strong>{p.documentNumber || "—"}</strong>
                    </div>
                    <div>
                      <span className="text-gray-500">Naissance :</span>{" "}
                      <strong>{p.dateOfBirth || "—"}</strong>
                    </div>
                    <div>
                      <span className="text-gray-500">Expiration :</span>{" "}
                      <strong>{p.documentExpiryDate || "—"}</strong>
                    </div>
                  </div>

                  <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {front && (
                      <a href={front} target="_blank" rel="noreferrer">
                        <img
                          src={front}
                          alt="Document recto"
                          className="w-full h-44 object-cover rounded-xl border"
                        />
                      </a>
                    )}

                    {back && (
                      <a href={back} target="_blank" rel="noreferrer">
                        <img
                          src={back}
                          alt="Document verso"
                          className="w-full h-44 object-cover rounded-xl border"
                        />
                      </a>
                    )}
                  </div>

                  <div className="mt-5 flex gap-2 justify-end">
                    <button
                      disabled={updatingId === user.id}
                      onClick={() => updateStatus(user.id, "rejected")}
                      className="px-4 py-2 rounded-xl bg-red-600 text-white disabled:opacity-50"
                    >
                      Rejeter
                    </button>

                    <button
                      disabled={updatingId === user.id}
                      onClick={() => updateStatus(user.id, "approved")}
                      className="px-4 py-2 rounded-xl bg-emerald-600 text-white disabled:opacity-50"
                    >
                      Approuver
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
